import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { syncQueue } from "@/lib/data";
import { pendingCount } from "@/lib/offline";

const SyncContext = createContext(null);

export function SyncProvider({ children }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const timer = useRef(null);

  const refreshPending = useCallback(async () => {
    setPending(await pendingCount());
  }, []);

  const runSync = useCallback(async () => {
    if (!navigator.onLine) return;
    setSyncing(true);
    try {
      await syncQueue();
    } catch {
      /* ignore */
    } finally {
      await refreshPending();
      setSyncing(false);
    }
  }, [refreshPending]);

  useEffect(() => {
    refreshPending();
    const onOnline = () => {
      setOnline(true);
      runSync();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // periodic pending refresh
    timer.current = setInterval(refreshPending, 4000);
    if (navigator.onLine) runSync();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SyncContext.Provider value={{ online, pending, syncing, runSync, refreshPending }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  return useContext(SyncContext);
}
